// Rate limiting utility for authentication attempts
import SHA256 from "crypto-js/sha256";
import { LOCAL_STORAGE_KEYS, STORAGE_FIELD_KEYS } from "./storageKeys";
interface RateLimitEntry {
  attempts: number;
  lastAttempt: number;
  blockedUntil?: number;
}

const RATE_LIMIT_CONFIG = {
  maxAttempts: 5,          // Maximum attempts allowed
  windowMs: 15 * 60 * 1000, // 15 minutes window
  blockDurationMs: 30 * 60 * 1000, // 30 minutes block duration
};

class RateLimiter {
  private static instance: RateLimiter;
  private storage = new Map<string, RateLimitEntry>();

  private constructor() {}

  static getInstance(): RateLimiter {
    if (!RateLimiter.instance) {
      RateLimiter.instance = new RateLimiter();
    }
    return RateLimiter.instance;
  }

  private hashIdentifier(email: string): string {
    const normalizedEmail = email.trim().toLowerCase();
    const hash = SHA256(normalizedEmail);
    return hash.toString();
  }

  private getStorageKey(email: string, action: 'signin' | 'signup'): string {
    const hashedEmail = this.hashIdentifier(email);
    const actionHash =
      action === 'signin'
        ? LOCAL_STORAGE_KEYS.rateLimitSignin
        : LOCAL_STORAGE_KEYS.rateLimitSignup;
    return `${LOCAL_STORAGE_KEYS.rateLimitPrefix}_${actionHash}_${hashedEmail}`;
  }

  private getEntry(key: string): RateLimitEntry {
    // Try to get from localStorage first, then fallback to memory
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Support both hashed-field and legacy-field formats
        if (
          parsed &&
          (STORAGE_FIELD_KEYS.attempts in parsed ||
            STORAGE_FIELD_KEYS.lastAttempt in parsed ||
            STORAGE_FIELD_KEYS.blockedUntil in parsed)
        ) {
          return {
            attempts: parsed[STORAGE_FIELD_KEYS.attempts] ?? 0,
            lastAttempt: parsed[STORAGE_FIELD_KEYS.lastAttempt] ?? 0,
            blockedUntil: parsed[STORAGE_FIELD_KEYS.blockedUntil],
          };
        }
        return parsed as RateLimitEntry;
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return this.storage.get(key) || { attempts: 0, lastAttempt: 0 };
  }

  private setEntry(key: string, entry: RateLimitEntry): void {
    // Store in both localStorage and memory
    try {
      const serialized = {
        [STORAGE_FIELD_KEYS.attempts]: entry.attempts,
        [STORAGE_FIELD_KEYS.lastAttempt]: entry.lastAttempt,
        ...(entry.blockedUntil !== undefined
          ? { [STORAGE_FIELD_KEYS.blockedUntil]: entry.blockedUntil }
          : {}),
      };
      localStorage.setItem(key, JSON.stringify(serialized));
      try {
        if (typeof document !== 'undefined') {
          const isSignin = key.includes(`_${LOCAL_STORAGE_KEYS.rateLimitSignin}_`);
          if (isSignin) {
            const parts = key.split('_');
            const hashedEmail = parts[parts.length - 1];
            const cookieName = `${hashedEmail}_in_lmt`;
            const value = JSON.stringify(serialized);
            const maxAge = 60 * 60 * 24 * 30;
            const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
            document.cookie = `${encodeURIComponent(cookieName)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Strict${isHttps ? '; Secure' : ''}`;
          }
        }
      } catch {}
    } catch (error) {
      console.warn('Failed to write to localStorage:', error);
    }
    this.storage.set(key, entry);
  }

  isBlocked(email: string, action: 'signin' | 'signup'): boolean {
    const key = this.getStorageKey(email, action);
    const entry = this.getEntry(key);
    const now = Date.now();

    // Check if currently blocked
    if (entry.blockedUntil && entry.blockedUntil > now) {
      return true;
    }

    // Clean up expired block
    if (entry.blockedUntil && entry.blockedUntil <= now) {
      entry.blockedUntil = undefined;
      entry.attempts = 0;
      this.setEntry(key, entry);
    }

    return false;
  }

  getBlockedTimeRemaining(email: string, action: 'signin' | 'signup'): number {
    const key = this.getStorageKey(email, action);
    const entry = this.getEntry(key);
    const now = Date.now();

    if (entry.blockedUntil && entry.blockedUntil > now) {
      return Math.ceil((entry.blockedUntil - now) / 1000); // Return seconds remaining
    }

    return 0;
  }

  recordAttempt(email: string, action: 'signin' | 'signup', success: boolean): void {
    const key = this.getStorageKey(email, action);
    const entry = this.getEntry(key);
    const now = Date.now();

    if (success) {
      // Reset on successful attempt
      entry.attempts = 0;
      entry.lastAttempt = now;
      entry.blockedUntil = undefined;
    } else {
      // Check if we're in a new window
      const timeSinceLastAttempt = now - entry.lastAttempt;
      if (timeSinceLastAttempt > RATE_LIMIT_CONFIG.windowMs) {
        // Reset attempts if window has passed
        entry.attempts = 1;
      } else {
        entry.attempts += 1;
      }

      entry.lastAttempt = now;

      // Block if max attempts reached
      if (entry.attempts >= RATE_LIMIT_CONFIG.maxAttempts) {
        entry.blockedUntil = now + RATE_LIMIT_CONFIG.blockDurationMs;
      }
    }

    this.setEntry(key, entry);

    try {
      if (action === 'signin' && typeof document !== 'undefined') {
        const cookieName = `${this.hashIdentifier(email)}_in_lmt`;
        const value = JSON.stringify({
          [STORAGE_FIELD_KEYS.attempts]: entry.attempts,
          [STORAGE_FIELD_KEYS.lastAttempt]: entry.lastAttempt,
          ...(entry.blockedUntil !== undefined
            ? { [STORAGE_FIELD_KEYS.blockedUntil]: entry.blockedUntil }
            : {}),
        });
        const maxAge = 60 * 60 * 24 * 30;
        const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
        document.cookie = `${encodeURIComponent(cookieName)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Strict${isHttps ? '; Secure' : ''}`;
      }
    } catch {}
  }

  getRemainingAttempts(email: string, action: 'signin' | 'signup'): number {
    const key = this.getStorageKey(email, action);
    const entry = this.getEntry(key);
    const now = Date.now();

    // If blocked, no attempts remaining
    if (entry.blockedUntil && entry.blockedUntil > now) {
      return 0;
    }

    // If window has expired, reset
    const timeSinceLastAttempt = now - entry.lastAttempt;
    if (timeSinceLastAttempt > RATE_LIMIT_CONFIG.windowMs) {
      return RATE_LIMIT_CONFIG.maxAttempts;
    }

    return Math.max(0, RATE_LIMIT_CONFIG.maxAttempts - entry.attempts);
  }

  formatBlockedTime(seconds: number): string {
    if (seconds <= 0) return '';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  }

  // Clear all rate limit data (useful for testing)
  clearAll(): void {
    this.storage.clear();
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('rateLimit_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }
}

export const rateLimiter = RateLimiter.getInstance();
export default rateLimiter;
